/**
 * S1-P6-WORK-QUEUE-ROUTING-SETTINGS-ROLE-ONLY-BUILD-01 - pure helpers + wire
 * types for the Work Routing Settings page (`/dashboard/work-routing/settings`).
 *
 * Pure by construction - no React, no fetch, no Supabase, no Date.now() -
 * mirroring the leads-workspace-view / work-routing-qa-view doctrine so every
 * parser, formatter, and access decision unit-tests without jsdom.
 *
 * SCOPE / SAFETY POSTURE:
 *   - The page is for the routing-configuration roles only: owner, gm_agm,
 *     regional_head. The backend (canConfigureRouting) is the real security
 *     boundary; this module's access decision only closes the UX gap.
 *   - The org-default scope option is OWNER-ONLY here (canEditOrgDefault),
 *     mirroring the backend's org-default-owner-only policy. A manager never
 *     sees it.
 *   - Routable roles are exactly receptionist / coc / regional_head / gm_agm /
 *     owner. doctor / patient and a specific-user target are NEVER offered.
 *   - PHI-free: routing_config carries no clinical field and no raw contact
 *     value; this module formats ids / clinic name / role / template /
 *     timestamps only.
 */

import { formatIstDateTime } from '@/lib/datetime-ist';
import type { DashboardMe } from '@/dashboard/types';

// --- Wire types (PHI-free; mirror api/routes/work-routing-settings.ts) ----

/**
 * The product-owned workflow template catalog. Frontend mirror of the backend
 * WORKFLOW_TEMPLATE_CODES (lib/work-routing/templates.ts), kept in lockstep by
 * review. The live page uses the server-returned `workflowTemplates` array; this
 * constant types the catalog and gives `new_lead_followup` first.
 */
export const WORKFLOW_TEMPLATE_CODES = [
  'new_lead_followup',
  'missed_call_callback',
  'walkin_patient_link_review',
  'lead_not_contacted_escalation',
  'appointment_confirmation_followup',
  'billing_entry',
] as const;
export type WorkflowTemplateCode = (typeof WORKFLOW_TEMPLATE_CODES)[number];

/**
 * The routable role subset. Frontend mirror of the backend ROUTABLE_ROLES;
 * doctor and patient are intentionally excluded (the page never offers them).
 */
export const ROUTABLE_ROLES = [
  'receptionist',
  'coc',
  'regional_head',
  'gm_agm',
  'owner',
] as const;
export type RoutableRole = (typeof ROUTABLE_ROLES)[number];

/**
 * A routing_config row as served by GET /api/work-routing/settings. Deliberately
 * carries NO target_user_id (a target-user picker is deferred).
 */
export interface RoutingConfigRow {
  id: number;
  organizationId: number;
  clinicId: number | null;
  workflowTemplateCode: string;
  targetRole: string;
  isActive: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

/** Non-PHI clinic picker option (name = clinics.name, never a contact value). */
export interface RoutingSettingsClinicOption {
  id: number;
  name: string | null;
}

/**
 * One who-changed-what audit entry for a routing rule (frontend mirror of the
 * backend RoutingAuditEntry). Read-only; actorName is null for a legacy row
 * recorded before actor tracking.
 */
export interface RoutingAuditEntry {
  entityId: string;
  action: string;
  actorName: string | null;
  actorRole: string | null;
  occurredAt: string | null;
  reasonCode: string | null;
}

/** GET /api/work-routing/settings response. */
export interface RoutingSettingsResponse {
  configs: RoutingConfigRow[];
  clinics: RoutingSettingsClinicOption[];
  workflowTemplates: string[];
  routableRoles: string[];
  /** Who-changed-what trail for the visible rules, newest first. */
  audit: RoutingAuditEntry[];
}

/** POST /api/work-routing/settings/:id/active request body. */
export interface RoutingLifecycleRequest {
  isActive: boolean;
}

/** POST /api/work-routing/settings/:id/active result. */
export interface RoutingLifecycleResult {
  outcome: 'deactivated' | 'reactivated' | 'unchanged';
  config: RoutingConfigRow;
}

/** PUT /api/work-routing/settings request body (organization_id is server-derived). */
export interface RoutingSettingUpdateRequest {
  clinicId: number | null;
  workflowTemplateCode: string;
  targetRole: string;
}

/** PUT result. */
export interface RoutingSettingUpdateResult {
  outcome: 'created' | 'updated';
  config: RoutingConfigRow;
}

// --- Access decision (role-only; no /me flag, no feature flag) -------------

/**
 * The frontend mirror of the backend CONFIGURE_ROUTING_ROLES. Used only for the
 * route guard / nav UX; the backend canConfigureRouting is the real boundary.
 */
export const CONFIGURE_ROUTING_ROLES: ReadonlySet<DashboardMe['role']> =
  new Set<DashboardMe['role']>(['owner', 'gm_agm', 'regional_head']);

export type RoutingSettingsAccessDecision =
  | { kind: 'wait' }
  | { kind: 'render' }
  | { kind: 'redirect'; to: string };

/**
 * Pure access decision for the routing-settings route guard. Mirrors
 * decideNavSectionAccess (role-view.ts): wait while loading; redirect to /login
 * on error or no scope; redirect to the caller's own homePath when the role is
 * outside the routing-configuration set; otherwise render.
 */
export function decideRoutingSettingsAccess(state: {
  loading: boolean;
  error: string | null;
  me: DashboardMe | null;
}): RoutingSettingsAccessDecision {
  if (state.loading) return { kind: 'wait' };
  if (state.error || !state.me) {
    return { kind: 'redirect', to: '/login' };
  }
  if (!CONFIGURE_ROUTING_ROLES.has(state.me.role)) {
    return { kind: 'redirect', to: state.me.homePath };
  }
  return { kind: 'render' };
}

/**
 * True only for the owner. Mirrors the backend org-default-owner-only policy:
 * gm_agm / regional_head edit clinic overrides only, never the org default.
 */
export function canEditOrgDefault(me: DashboardMe | null): boolean {
  return me?.role === 'owner';
}

// --- Scope picker (org-default OR an in-scope clinic) ----------------------

/** Sentinel for the "organization default" scope option ('' in the <select>). */
export const ORG_DEFAULT_SCOPE_VALUE = '';

export interface ScopeOption {
  value: string;
  label: string;
}

/** Display label for a clinic option (falls back to "Clinic <id>" when unnamed). */
export function clinicLabel(clinic: RoutingSettingsClinicOption): string {
  return clinic.name !== null && clinic.name.trim().length > 0
    ? clinic.name
    : `Clinic ${clinic.id}`;
}

/**
 * The scope dropdown options for the caller. The org-default option is included
 * ONLY for the owner (canEditOrgDefault); every authorized role gets its
 * in-scope clinics.
 */
export function scopeOptions(
  me: DashboardMe | null,
  clinics: readonly RoutingSettingsClinicOption[],
): ScopeOption[] {
  const options: ScopeOption[] = [];
  if (canEditOrgDefault(me)) {
    options.push({ value: ORG_DEFAULT_SCOPE_VALUE, label: 'Organization default' });
  }
  for (const clinic of clinics) {
    options.push({ value: String(clinic.id), label: clinicLabel(clinic) });
  }
  return options;
}

/** Parse a scope dropdown value: '' = org default (null); else the clinic id. */
export function parseScopeValue(value: string): number | null {
  if (value === ORG_DEFAULT_SCOPE_VALUE) return null;
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 ? n : null;
}

/**
 * Build the PUT body from the form state. By contract it carries exactly
 * { clinicId, workflowTemplateCode, targetRole } - organization_id is derived
 * server-side from the scope, and target_user_id is never sent (deferred).
 */
export function buildUpdateRequest(
  scopeValue: string,
  workflowTemplateCode: string,
  targetRole: string,
): RoutingSettingUpdateRequest {
  return {
    clinicId: parseScopeValue(scopeValue),
    workflowTemplateCode,
    targetRole,
  };
}

// --- Display formatting (PHI-free) ----------------------------------------

const ROLE_LABELS: Readonly<Record<string, string>> = {
  receptionist: 'Receptionist',
  coc: 'COC',
  regional_head: 'Regional head',
  gm_agm: 'GM / AGM',
  owner: 'Owner',
  // FOLLOWUP-OWNERSHIP-ROUTING-CONFIG-V1: the follow-up ownership section
  // labels roles the rules table never offered (the window-read grant may
  // name the doctor; the owner vocabulary includes the blended call-center
  // key). Display labels only -- vocabulary comes from the server.
  doctor: 'Doctor',
  call_center_agent: 'Call center agent',
};

/** Human label for a routable role (falls back to the raw key). */
export function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

const TEMPLATE_LABELS: Readonly<Record<string, string>> = {
  new_lead_followup: 'New lead follow-up',
  missed_call_callback: 'Missed call callback',
  walkin_patient_link_review: 'Walk-in patient link review',
  lead_not_contacted_escalation: 'Lead not contacted escalation',
  appointment_confirmation_followup: 'Appointment confirmation follow-up',
  billing_entry: 'Billing entry (who records payments)',
};

/** Human label for a workflow template code (humanized fallback for unknowns). */
export function templateLabel(code: string): string {
  const known = TEMPLATE_LABELS[code];
  if (known) return known;
  const spaced = code.replace(/_/g, ' ').trim();
  return spaced.length > 0
    ? spaced.charAt(0).toUpperCase() + spaced.slice(1)
    : code;
}

/** Scope label for a current rule row ("Organization default" or the clinic). */
export function scopeLabelForConfig(
  row: RoutingConfigRow,
  clinics: readonly RoutingSettingsClinicOption[],
): string {
  if (row.clinicId === null) return 'Organization default';
  const clinic = clinics.find((c) => c.id === row.clinicId);
  return clinic ? clinicLabel(clinic) : `Clinic ${row.clinicId}`;
}

/** Column headers for the current-rules table (display order). */
export const ROUTING_RULE_COLUMNS: readonly string[] = [
  'Scope',
  'Work type',
  'Routed to',
  'Active',
];

/** One current-rule row as ordered display cells (PHI-free). */
export function routingRuleCells(
  row: RoutingConfigRow,
  clinics: readonly RoutingSettingsClinicOption[],
): string[] {
  return [
    scopeLabelForConfig(row, clinics),
    templateLabel(row.workflowTemplateCode),
    roleLabel(row.targetRole),
    row.isActive ? 'Active' : 'Inactive',
  ];
}

// --- Copy (mandated wording) ----------------------------------------------

export const ROUTING_SETTINGS_TITLE = 'Work Routing Settings';
export const ROUTING_SETTINGS_INTRO =
  'Choose which role receives each type of work. Existing open work items are not reassigned; changes apply to new work.';
export const ROUTING_SETTINGS_EMPTY =
  'No routing rule is configured yet. New work without a matching rule lands unassigned until a default or clinic override is saved.';

/**
 * Actionable save-failure copy (S1-P8 polish). Replaces the bare exception
 * message with a recoverable next step: the two real causes of a PUT failure
 * are a transient/refresh issue, an access denial (managers cannot set the org
 * default), or a stale frontend talking to an older backend.
 */
export const ROUTING_SETTINGS_SAVE_ERROR =
  'Could not save routing rule. Refresh and try again; if it continues, check your access or that the latest backend is deployed.';

/**
 * Build the displayed save-error message: the actionable copy above, with the
 * specific backend reason appended in parentheses when one is available (so an
 * access denial is never hidden behind the generic guidance). Pure.
 */
export function routingSaveErrorMessage(error: unknown): string {
  const detail = error instanceof Error ? error.message.trim() : '';
  return detail.length > 0
    ? `${ROUTING_SETTINGS_SAVE_ERROR} (${detail})`
    : ROUTING_SETTINGS_SAVE_ERROR;
}

// =====================================================================
// WORK-ROUTING-ADMIN-GOVERNANCE-V1 - lifecycle + precedence + who-changed-what
// pure helpers (the founder's 2026-07-22 finding: rules could be added and
// replaced but never deactivated, never explained, never attributed). All pure:
// no React, no fetch, no Date.now(). The backend is the security boundary; these
// helpers only render the truth the resolver + audit plane already hold.
// =====================================================================

const UNASSIGNED_LABEL = 'Unassigned';

/** True for the ONE org-default rule (clinic_id null). */
export function isOrgDefault(row: RoutingConfigRow): boolean {
  return row.clinicId === null;
}

// --- Who-changed-what (from the existing audit read) -----------------

/** The reason_code / action -> a human label for a routing audit entry. */
export function auditActionLabel(entry: RoutingAuditEntry): string {
  switch (entry.reasonCode) {
    case 'routing_config_deactivate':
      return 'Deactivated';
    case 'routing_config_reactivate':
      return 'Reactivated';
    case 'routing_config_update':
      return entry.action === 'create' ? 'Created' : 'Role changed';
    default:
      // Unknown reason_code: fall back to the raw action verb.
      if (entry.action === 'create') return 'Created';
      if (entry.action === 'update') return 'Updated';
      return entry.action.length > 0 ? entry.action : 'Changed';
  }
}

/**
 * The actor display for an audit entry. Names the person + role when known;
 * renders honestly when a legacy row predates actor tracking (no actorName).
 */
export function auditActorLabel(entry: RoutingAuditEntry): string {
  if (entry.actorName === null || entry.actorName.trim().length === 0) {
    return 'recorded before actor tracking';
  }
  const role = entry.actorRole ? ` (${roleLabel(entry.actorRole)})` : '';
  return `${entry.actorName}${role}`;
}

/** Zone-labeled timestamp for an audit entry ("19 Jul 2026, 2:30 PM IST"), or a dash. */
export function auditWhenLabel(occurredAt: string | null): string {
  if (occurredAt === null) return '-';
  const formatted = formatIstDateTime(occurredAt);
  return formatted.length > 0 ? `${formatted} IST` : '-';
}

/** All audit entries for one rule id, preserving the newest-first order. */
export function auditForRule(
  audit: readonly RoutingAuditEntry[],
  ruleId: number,
): RoutingAuditEntry[] {
  const key = String(ruleId);
  return audit.filter((e) => e.entityId === key);
}

/** The most recent audit entry for a rule, or null when none is recorded. */
export function latestAuditForRule(
  audit: readonly RoutingAuditEntry[],
  ruleId: number,
): RoutingAuditEntry | null {
  const rows = auditForRule(audit, ruleId);
  return rows.length > 0 ? rows[0]! : null;
}

/**
 * One-line who-changed-what summary for a rule's table row, e.g.
 * "Role changed by Priya (Owner), 19 Jul 2026, 2:30 PM IST". When no audit row
 * exists yet, states so honestly.
 */
export function rowChangeSummary(
  audit: readonly RoutingAuditEntry[],
  ruleId: number,
): string {
  const latest = latestAuditForRule(audit, ruleId);
  if (latest === null) return 'No recorded change';
  return `${auditActionLabel(latest)} by ${auditActorLabel(latest)}, ${auditWhenLabel(latest.occurredAt)}`;
}

// --- Precedence made visible (renders the resolver's truth) ----------

/**
 * The active clinic-override rules for a work type, sorted by clinic label.
 * These take precedence over the org default for their clinic (the resolver:
 * active clinic rule -> active org default -> unassigned).
 */
export function activeClinicRulesForTemplate(
  configs: readonly RoutingConfigRow[],
  clinics: readonly RoutingSettingsClinicOption[],
  workflowTemplateCode: string,
): Array<{ row: RoutingConfigRow; clinicLabel: string }> {
  return configs
    .filter(
      (r) =>
        r.isActive &&
        !isOrgDefault(r) &&
        r.workflowTemplateCode === workflowTemplateCode,
    )
    .map((row) => ({ row, clinicLabel: scopeLabelForConfig(row, clinics) }))
    .sort((a, b) => a.clinicLabel.localeCompare(b.clinicLabel));
}

/** The single active org-default rule for a work type, or null. */
export function activeOrgDefaultForTemplate(
  configs: readonly RoutingConfigRow[],
  workflowTemplateCode: string,
): RoutingConfigRow | null {
  return (
    configs.find(
      (r) =>
        r.isActive &&
        isOrgDefault(r) &&
        r.workflowTemplateCode === workflowTemplateCode,
    ) ?? null
  );
}

export interface PrecedenceGroup {
  workflowTemplateCode: string;
  templateLabel: string;
  orgDefaultRole: string | null;
  clinicRules: Array<{ row: RoutingConfigRow; clinicLabel: string }>;
  /** One-line resolution statement per the resolver's precedence order. */
  resolutionLine: string;
}

/**
 * Build the precedence groups (one per work type that has any active rule),
 * stating the resolution order the resolver implements: a clinic's own active
 * rule wins, else the active organization default, else unassigned. No engine
 * change - this only renders the existing resolver's truth.
 */
export function precedenceGroups(
  configs: readonly RoutingConfigRow[],
  clinics: readonly RoutingSettingsClinicOption[],
): PrecedenceGroup[] {
  const templates = Array.from(
    new Set(
      configs.filter((r) => r.isActive).map((r) => r.workflowTemplateCode),
    ),
  ).sort((a, b) => templateLabel(a).localeCompare(templateLabel(b)));

  return templates.map((code) => {
    const orgDefault = activeOrgDefaultForTemplate(configs, code);
    const orgDefaultRole = orgDefault ? orgDefault.targetRole : null;
    const clinicRules = activeClinicRulesForTemplate(configs, clinics, code);
    const tail =
      orgDefaultRole !== null
        ? `Organization default: ${roleLabel(orgDefaultRole)}`
        : `no organization default -> ${UNASSIGNED_LABEL}`;
    const resolutionLine =
      clinicRules.length > 0
        ? `Clinic rule (where set) -> ${tail}`
        : `${tail}`;
    return {
      workflowTemplateCode: code,
      templateLabel: templateLabel(code),
      orgDefaultRole,
      clinicRules,
      resolutionLine,
    };
  });
}

// --- Safety rails: shadow-confirm + lifecycle confirm copy ------------

/**
 * The active rule a new save would REPLACE: the existing active rule for the
 * SAME scope + work type. The writer updates that row in place, so saving is a
 * replace, not an add - the UI confirms first, naming the rule being shadowed.
 * Returns null when nothing active exists at that scope+template (a true create).
 */
export function shadowedActiveRule(
  scopeValue: string,
  workflowTemplateCode: string,
  configs: readonly RoutingConfigRow[],
): RoutingConfigRow | null {
  const clinicId = parseScopeValue(scopeValue);
  return (
    configs.find(
      (r) =>
        r.isActive &&
        r.workflowTemplateCode === workflowTemplateCode &&
        r.clinicId === clinicId,
    ) ?? null
  );
}

/**
 * The confirm-before-replace message: names the shadowed rule's current target
 * and the incoming one, so the operator knows a save overwrites, never stacks.
 */
export function replaceConfirmMessage(
  shadowed: RoutingConfigRow,
  newRole: string,
  clinics: readonly RoutingSettingsClinicOption[],
): string {
  const scope = scopeLabelForConfig(shadowed, clinics);
  const work = templateLabel(shadowed.workflowTemplateCode);
  return `A rule already routes ${work} at ${scope} to ${roleLabel(shadowed.targetRole)}. Saving replaces it with ${roleLabel(newRole)} for the same scope - the old target is not kept as a separate rule.`;
}

/**
 * What a DEACTIVATE changes, stated exactly per the resolver's fallback: new
 * work of this type at this scope falls back to the next rule in precedence.
 */
export function deactivateConfirmMessage(
  row: RoutingConfigRow,
  configs: readonly RoutingConfigRow[],
  clinics: readonly RoutingSettingsClinicOption[],
): string {
  const work = templateLabel(row.workflowTemplateCode);
  if (isOrgDefault(row)) {
    return `New ${work} work at clinics without their own rule will fall back to ${UNASSIGNED_LABEL} until an organization default is set again. Clinics with their own active rule are unaffected. You can reactivate this later.`;
  }
  const scope = scopeLabelForConfig(row, clinics);
  const orgDefault = activeOrgDefaultForTemplate(
    configs,
    row.workflowTemplateCode,
  );
  const fallback =
    orgDefault !== null
      ? `the organization default (${roleLabel(orgDefault.targetRole)})`
      : `${UNASSIGNED_LABEL} (no active organization default)`;
  return `New ${work} work at ${scope} will fall back to ${fallback}. You can reactivate this clinic rule later.`;
}

/** What a REACTIVATE changes: this scope routes to the rule's role again. */
export function reactivateConfirmMessage(
  row: RoutingConfigRow,
  clinics: readonly RoutingSettingsClinicOption[],
): string {
  const work = templateLabel(row.workflowTemplateCode);
  const scope = isOrgDefault(row)
    ? 'clinics without their own rule'
    : scopeLabelForConfig(row, clinics);
  return `New ${work} work at ${scope} will route to ${roleLabel(row.targetRole)} again.`;
}

/**
 * A rule is the LAST active rule for its work type when no OTHER active rule
 * (any scope) shares its template. The org-default row cannot be deactivated in
 * that state (the fallback must always exist) - the frontend disables the
 * control and explains; the backend enforces it regardless.
 */
export function isLastActiveRuleForTemplate(
  row: RoutingConfigRow,
  configs: readonly RoutingConfigRow[],
): boolean {
  const others = configs.filter(
    (r) =>
      r.isActive &&
      r.id !== row.id &&
      r.workflowTemplateCode === row.workflowTemplateCode,
  );
  return others.length === 0;
}

/**
 * Whether the org-default deactivate control must be DISABLED for this row: it
 * is the org default AND the last active rule for its work type. Mirrors the
 * backend last-rule guard so the UI never offers an action the server rejects.
 */
export function isDeactivateBlocked(
  row: RoutingConfigRow,
  configs: readonly RoutingConfigRow[],
): boolean {
  return (
    row.isActive &&
    isOrgDefault(row) &&
    isLastActiveRuleForTemplate(row, configs)
  );
}

/**
 * Whether the caller may run lifecycle / edit writes on THIS row, mirroring the
 * backend row-level authority (no widening): an org-default row is owner-only;
 * a clinic-override row is writable by any configuration role whose scope
 * includes that clinic. The backend remains the real boundary.
 */
export function canWriteRow(me: DashboardMe | null, row: RoutingConfigRow): boolean {
  if (me === null) return false;
  if (!CONFIGURE_ROUTING_ROLES.has(me.role)) return false;
  if (isOrgDefault(row)) return me.role === 'owner';
  return me.clinicIds.includes(row.clinicId as number);
}

// =====================================================================
// FOLLOWUP-OWNERSHIP-ROUTING-CONFIG-V1 - the Follow-up ownership section
// (pure wire types + helpers; tenant ruling docs/requirements/
// partha-dental/PARTHA-FOLLOWUP-OWNERSHIP-2026-08-21.md). All pure: no
// React, no fetch, no Date.now(). The backend (canConfigureRouting + the
// page's org-default write policy) is the security boundary; these helpers
// only render and pre-validate.
// =====================================================================

/** The five org values (wire mirror of the backend
 *  FollowupOwnershipConfigWire -- role tokens, a day count, a clock-basis
 *  token; PHI-free). */
export interface FollowupOwnershipValues {
  ownerBeforeBoundary: string;
  ownerAfterBoundary: string;
  boundaryDays: number;
  clockStartsAt: string;
  windowReadRoles: string[];
}

/** GET /api/work-routing/followup-ownership response. */
export interface FollowupOwnershipSettingsResponse {
  values: FollowupOwnershipValues;
  ownerRoleOptions: string[];
  windowReadRoleOptions: string[];
  clockBasisOptions: string[];
  /** The page's org-default write policy, echoed; the UI disables what the
   *  caller cannot save. */
  canEdit: boolean;
}

const CLOCK_BASIS_LABELS: Readonly<Record<string, string>> = {
  visit_date: 'Visit date',
};

/** Human label for a clock-basis token (humanized fallback for unknowns). */
export function clockBasisLabel(basis: string): string {
  const known = CLOCK_BASIS_LABELS[basis];
  if (known) return known;
  const spaced = basis.replace(/_/g, ' ').trim();
  return spaced.length > 0
    ? spaced.charAt(0).toUpperCase() + spaced.slice(1)
    : basis;
}

/**
 * The one-line statement of the rule the org currently runs -- rendered on
 * the glass so the operator reads the routing as a sentence, not a form.
 * Boundary 0 states the degenerate (Deepak-model) shape honestly: the
 * after-boundary owner owns every case from day 0 and no window exists.
 */
export function followupOwnershipSummaryLine(
  values: FollowupOwnershipValues,
): string {
  const clock = clockBasisLabel(values.clockStartsAt).toLowerCase();
  if (values.boundaryDays <= 0) {
    return `${roleLabel(values.ownerAfterBoundary)} owns every follow-up from day 0 (clock: ${clock}). No handoff window.`;
  }
  const readers =
    values.windowReadRoles.length > 0
      ? ` During the window ${values.windowReadRoles.map(roleLabel).join(', ')} can see the case, read-only.`
      : '';
  return `${roleLabel(values.ownerBeforeBoundary)} owns days 0-${values.boundaryDays - 1} (own clinic, clock: ${clock}); ${roleLabel(values.ownerAfterBoundary)} owns day ${values.boundaryDays} onward.${readers}`;
}

/** Toggle one role in the window-read grant (checkbox semantics; order
 *  preserved for existing members, appended for new). */
export function toggleWindowReadRole(
  roles: readonly string[],
  role: string,
): string[] {
  return roles.includes(role)
    ? roles.filter((r) => r !== role)
    : [...roles, role];
}

export type FollowupOwnershipFormResult =
  | { ok: true; request: FollowupOwnershipValues }
  | { ok: false; error: string };

/**
 * Build the PUT body from the form state, pre-validating the day count
 * client-side (whole number >= 0; ZERO IS VALID -- it is the COC-from-day-0
 * model). The server re-validates everything against the controlled
 * vocabularies regardless.
 */
export function buildFollowupOwnershipRequest(form: {
  ownerBeforeBoundary: string;
  ownerAfterBoundary: string;
  boundaryDaysText: string;
  clockStartsAt: string;
  windowReadRoles: readonly string[];
}): FollowupOwnershipFormResult {
  const trimmed = form.boundaryDaysText.trim();
  const boundaryDays = Number(trimmed);
  if (
    trimmed === '' ||
    !Number.isInteger(boundaryDays) ||
    boundaryDays < 0
  ) {
    return {
      ok: false,
      error: 'Handoff after must be a whole number of days (0 or more).',
    };
  }
  return {
    ok: true,
    request: {
      ownerBeforeBoundary: form.ownerBeforeBoundary,
      ownerAfterBoundary: form.ownerAfterBoundary,
      boundaryDays,
      clockStartsAt: form.clockStartsAt,
      windowReadRoles: [...form.windowReadRoles],
    },
  };
}
